import { FC, ReactElement, useEffect, useState, memo, useMemo } from 'react';

import clsx from 'clsx';
import { TbChevronDown } from 'react-icons/tb';

import {
  Accordion,
  AccordionControlProps,
  AccordionPanelProps,
  AccordionProps,
  Group,
  Text
} from '@ui';

import { AdaptiveFormRows } from '../../AdaptiveFormRows';
import { AdaptiveFormConfig } from '../../types';

import classes from './AccordionSection.module.css';

export interface AccordionSectionProps extends AccordionProps {
  // if true, accordion will not expand on click
  restrictExpansion?: boolean;
  // if true, accordion be expanded by default
  expanded?: boolean;
  // item value
  itemValue: string;
  // title to render inside the accordion
  title?: string;
  // header to render inside the accordion
  header?: ReactElement;
  // titleRightSection
  titleRightSection?: ReactElement;
  // Form to render
  formConfig?: AdaptiveFormConfig;
  controlProps?: AccordionControlProps;
  panelProps?: AccordionPanelProps;
  // show / hide bottom border
  bordered?: boolean;
  // if true, AdaptiveFormRows will be wrapped inside a default wrapper
  withDefaultWrapperForAdaptiveFormRows?: boolean;
  // function to render form component
  renderFormComponent?: () => ReactElement;
}
/**
 *
 * @description AccordionSection renders a form inside an accordion.
 *
 */
const AccordionSection: FC<AccordionSectionProps> = memo(
  ({
    restrictExpansion = false,
    expanded,
    title,
    header,
    formConfig,
    controlProps,
    panelProps,
    itemValue,
    titleRightSection,
    bordered = true,
    withDefaultWrapperForAdaptiveFormRows = true,
    renderFormComponent,
    ...rest
  }) => {
    const [_value, setValue] = useState<string | null>(null);

    useEffect(() => {
      setValue(expanded ? itemValue : null);
    }, [expanded, itemValue]);

    const handleChange = (value: string | null) => {
      if (restrictExpansion) return;
      setValue(value);
    };

    // Memoize the header content to prevent unnecessary re-renders
    const headerContent = useMemo(() => {
      if (title) {
        return (
          <Group justify="space-between">
            <Text fz={{ base: 14, sm: 18 }} fw={600}>
              {title}
            </Text>
            {titleRightSection ? titleRightSection : null}
          </Group>
        );
      }
      return header;
    }, [title, titleRightSection, header]);

    // Memoize the panel content
    const panelContent = useMemo(() => {
      const shouldRenderContent =
        !restrictExpansion || (restrictExpansion && expanded);

      if (!shouldRenderContent) return null;

      if (typeof renderFormComponent === 'function') {
        return renderFormComponent();
      }

      return (
        <AdaptiveFormRows
          withDefaultWrapper={withDefaultWrapperForAdaptiveFormRows}
          config={formConfig!}
        />
      );
    }, [
      restrictExpansion,
      expanded,
      renderFormComponent,
      withDefaultWrapperForAdaptiveFormRows,
      formConfig
    ]);

    return (
      <Accordion
        value={_value}
        onChange={handleChange}
        chevron={<TbChevronDown size={24} />}
        classNames={{
          root: classes.root,
          content: classes.content,
          item: clsx({ [classes.noBorder]: !bordered }),
          control: classes.control
        }}
        w="100%"
        {...rest}
      >
        <Accordion.Item value={itemValue}>
          <Accordion.Control {...controlProps}>{headerContent}</Accordion.Control>
          <Accordion.Panel {...panelProps}>{panelContent}</Accordion.Panel>
        </Accordion.Item>
      </Accordion>
    );
  }
);

AccordionSection.displayName = 'AccordionSection';

export { AccordionSection };
